import { animate, animateChild, query, state, style, transition, trigger } from '@angular/animations';
import { CdkStepper, StepperOrientation } from '@angular/cdk/stepper';
import { Component, ContentChildren, HostBinding, Input, OnInit, QueryList } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { MuiAnimationTiming } from '../../utility';
import { MuiWizardStep } from '../wizard-step/mui-wizard-step.component';


const stateAnimation = trigger('wizardAnimation', [
  state('previous', style({ transform: 'translate3d(-15%, 0, 0)', opacity: 0, visibility: 'hidden' })),
  state('current', style({ transform: 'none', opacity: 1, visibility: 'visible' })),
  state('next', style({ transform: 'translate3d(15%, 0, 0)', opacity: 0, visibility: 'hidden' })),
  transition('* => *', [animate(MuiAnimationTiming.WIZARD_STEP_TRANSITION), query('@*', animateChild(), { optional: true })])
]);

let nextId = 0;

@Component({
  selector: 'mui-wizard',
  templateUrl: './mui-wizard.component.html',
  exportAs: 'muiWizard',
  providers: [{ provide: CdkStepper, useExisting: MuiWizard }],
  animations: [stateAnimation],
  styles: [
    `
      :host {
        display: block;
      }
    `
  ],
  /* We add metadata inputs and outputs here because we use the ones from the base class and they are not recognized by the
   Angular language service in templates otherwise. */
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['linear'],
  // eslint-disable-next-line @angular-eslint/no-outputs-metadata-property
  outputs: ['selectionChange']
})
export class MuiWizard extends CdkStepper implements OnInit {
  @Input()
  @HostBinding('attr.id')
  id = `mui-wizard-${nextId++}`;

  /** Disable the scrolling behaviour on step change events. */
  @Input()
  disableScroll = false;

  /** Element that should be scrolled to instead of the window top position. */
  @Input()
  scrollAnchor: HTMLElement | string = '';

  /** Hide the inline titles of all wizard steps. */
  @Input()
  hideInlineTitles = false;

  /** Hide the wizard's own stepper that is displayed at the top. */
  @Input()
  hideWizardStepper = false;

  /** Steps that the stepper holds. */
  @ContentChildren(MuiWizardStep, { descendants: true })
  declare _steps: QueryList<MuiWizardStep>;

  @HostBinding('attr.role')
  readonly role = 'tablist';

  @HostBinding('attr.aria-orientation')
  get orientation(): StepperOrientation {
    return 'horizontal'; // vertical is not supported currently
  }

  protected animating = false;

  @HostBinding('class.mui-wizard')
  private _baseClass = true;

  _getStepLabelId(i: number): string {
    return `${this.id}-label-${i}`;
  }

  _getStepContentId(i: number): string {
    return `${this.id}-content-${i}`;
  }

  ngOnInit(): void {
    this.selectionChange.pipe(takeUntil(this._destroyed)).subscribe(() => this._handleScrollOnSelectionChange());
  }

  protected onAnimationStart(): void {
    this.animating = true;
  }

  protected onAnimationDone(stepIndex: number): void {
    if (stepIndex === this.selectedIndex) {
      this.animating = false;
    }
  }

  /**
   * When the selected step changes, ensure that the viewport is scroll to a position where the top of the wizard can be seen.
   */
  protected _handleScrollOnSelectionChange(): void {
    if (!this.disableScroll) {
      if (this.scrollAnchor) {
        if (typeof this.scrollAnchor === 'string') {
          const element = document.querySelector(this.scrollAnchor);
          if (element) {
            element.scrollIntoView();
          }
        } else if (this.scrollAnchor instanceof HTMLElement) {
          this.scrollAnchor.scrollIntoView({
            block: 'start',
            inline: 'start',
            behavior: 'smooth'
          });
        }
      } else {
        window.scrollTo(0, 0);
      }
    }
  }
}
